import classNames from 'classnames';
import { m } from 'motion/react';
import { type ReactElement, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import Activity from '@/components/Icons/Activity';
import ArrowsClockwise from '@/components/Icons/ArrowsClockwise';
import CaretDown from '@/components/Icons/CaretDown';
import CaretUp from '@/components/Icons/CaretUp';
import CheckCircle from '@/components/Icons/CheckCircle';
import WarningCircle from '@/components/Icons/WarningCircle';
import { useIsMobile } from '@/hooks';
import { JobStatus, JobType } from '@/providers/Jobs/Context';
import { mixpanelTrack } from '@/providers/Mixpanel';
import { SIZES } from '@/styles/media';

import isDependency from '../isDependency';
import useJobContext from '../useJobContext';

import AsyncJob from './AsyncJob';
import BeforeUnload from './BeforeUnload';
import s from './JobsContainer.module.scss';

export default function JobContainer(): JSX.Element | null {
  const { jobs } = useJobContext();

  const [isExpanded, setIsExpanded] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState(true);
  const showActivityDot = !jobs.length;

  const isMobile = useIsMobile(SIZES.lg);
  const failedJobs = jobs.filter((j) => j.status === JobStatus.FAILED);
  const pendingJobs = jobs.filter((j) => j.status === JobStatus.IN_PROGRESS);
  const completedJobs = jobs.filter((j) => j.status === JobStatus.COMPLETED);
  const jobsToShow = jobs.filter((job) => !isDependency(job));

  useEffect(() => {
    const handleStatusChange = (): void => {
      setOnlineStatus(navigator.onLine);
    };

    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [onlineStatus]);

  useEffect(() => {
    if (isExpanded) {
      mixpanelTrack('Async Job Activity Log Opened');
    }
  }, [isExpanded]);

  const handleToggleExpanded = (): void => setIsExpanded(!isExpanded);

  const getJobText = (): string => {
    if (failedJobs.length > 0) {
      return `${failedJobs.length} failed job${failedJobs.length === 1 ? '' : 's'}`;
    }
    if (pendingJobs.length > 0) {
      return `${pendingJobs.length} item${pendingJobs.length === 1 ? ' is being processed' : 's are being processed'}...`;
    }
    return `${completedJobs.length} processed item${completedJobs.length === 1 ? '' : 's'}`;
  };

  const getIconBefore = (): ReactElement => {
    if (showActivityDot) {
      return <Activity color='var(--color-gray-100)' />;
    }
    if (failedJobs.length > 0) {
      return <WarningCircle weight='fill' color='var(--color-error)' />;
    }
    if (pendingJobs.length > 0) {
      return <ArrowsClockwise weight='fill' color='var(--color-gray-100)' />;
    }
    return <CheckCircle weight='fill' color='var(--color-success)' />;
  };

  if (!jobs.length && onlineStatus) return null;

  return (
    <m.div
      initial={{ y: -40 }}
      animate={{ y: 0 }}
      className={classNames(s.wrapper, {
        [s['wrapper--expanded']]: isExpanded,
        [s['wrapper--relative']]: showActivityDot,
      })}
    >
      {pendingJobs.length > 0 && <BeforeUnload />}
      <Buttons
        isFlat={isMobile}
        isSmall
        isFullWidth
        className={classNames(s.jobsButton, {
          [s.expandButton]: isMobile,
          [s.activityLogButton]: !jobsToShow.length,
          [s.ai]: !isExpanded && jobsToShow.find((j) => j.type === JobType.AI_JOB || j.type === JobType.AI_LIST_AGENT),
        })}
        iconAfter={jobsToShow.length ? <>{isExpanded ? <CaretUp /> : <CaretDown />}</> : undefined}
        iconBefore={
          <m.div
            style={{ marginBlockStart: 'auto' }}
            animate={pendingJobs.length > 0 ? { rotate: 360 } : {}}
            transition={
              pendingJobs.length > 0
                ? {
                    duration: 1,
                    ease: 'linear',
                    repeat: Infinity,
                    repeatType: 'loop',
                  }
                : {}
            }
          >
            {getIconBefore()}
          </m.div>
        }
        onClick={handleToggleExpanded}
      >
        {showActivityDot && (
          <div
            className={s.activityDot}
            style={{
              backgroundColor: onlineStatus ? 'var(--color-success)' : 'var(--color-error)',
            }}
          />
        )}
        <span
          className={classNames(
            'body-sm',
            s.jobsButtonText,
            !isExpanded && jobsToShow.find((j) => j.type === JobType.AI_JOB) && 'ai-label',
          )}
        >
          {!!jobs.length && getJobText()}
          {!jobs.length && (
            <span
              className={classNames(s['connection-status'], {
                [s['connection-status--unstable']]: !onlineStatus,
              })}
            >
              {onlineStatus ? 'Connection stable' : 'Connection unstable'}
            </span>
          )}
        </span>
      </Buttons>
      <div className={s.jobContent} style={{ display: isExpanded ? 'block' : 'none' }}>
        <div className={s.jobList}>
          {!!jobsToShow.length && <p className={classNames('caption', s.jobsSectionText)}>Ongoing jobs:</p>}
          {jobsToShow.map((job) => (
            <AsyncJob key={job.id} job={job} />
          ))}
        </div>
      </div>
    </m.div>
  );
}
